import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import Logo from './Logo';
import { prefersReducedMotion } from '../../motion/tokens';

const KEY = 'bp-intro-seen';

// Cinematic opener, once per session: the mark drops in from the top, the
// wordmark settles beside it, then the curtain wipes top-down to reveal the
// page. Skipped for reduced-motion users and on repeat visits in the session.
export default function IntroReveal() {
  const [show, setShow] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (prefersReducedMotion()) return false;
    try {
      return !window.sessionStorage.getItem(KEY);
    } catch {
      return true;
    }
  });
  const ref = useRef(null);

  useEffect(() => {
    if (!show || !ref.current) return undefined;
    document.documentElement.style.overflow = 'hidden';
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: 'expo.out' },
        onComplete: () => {
          document.documentElement.style.overflow = '';
          try { window.sessionStorage.setItem(KEY, '1'); } catch { /* private mode */ }
          setShow(false);
        },
      });
      tl.fromTo('[data-intro-mark]', { y: -140, opacity: 0, rotate: -6 }, { y: 0, opacity: 1, rotate: 0, duration: 0.9 })
        .fromTo('[data-intro-word] span', { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, stagger: 0.08 }, '-=0.45')
        .to('[data-intro-line]', { scaleX: 1, duration: 0.6, ease: 'power3.inOut' }, '-=0.3')
        .to('[data-intro-inner]', { y: -24, opacity: 0, duration: 0.5, ease: 'power2.in' }, '+=0.25')
        .to(ref.current, { clipPath: 'inset(100% 0 0 0)', duration: 0.9, ease: 'expo.inOut' }, '-=0.2');
    }, ref);
    return () => {
      ctx.revert();
      document.documentElement.style.overflow = '';
    };
  }, [show]);

  if (!show) return null;
  return (
    <div ref={ref} className="fixed inset-0 z-[90] flex items-center justify-center bg-ink" style={{ clipPath: 'inset(0 0 0 0)' }} aria-hidden="true">
      <div data-intro-inner className="flex flex-col items-center gap-6">
        <div data-intro-mark className="will-change-transform">
          <Logo className="h-20 w-20 md:h-28 md:w-28" />
        </div>
        <p data-intro-word className="font-display text-4xl font-normal tracking-tight md:text-6xl">
          <span className="inline-block">Blu</span>
          <span className="inline-block text-sky">prise</span>
        </p>
        <div data-intro-line className="h-px w-40 origin-left scale-x-0 bg-sky" />
      </div>
    </div>
  );
}
